import React, { useState } from "react";
import {
  FaArrowRight,
  FaCheckCircle,
  FaClipboardList,
  FaExternalLinkAlt,
  FaGoogle,
  FaInfoCircle,
  FaLock,
  FaMobileAlt,
  FaPaperPlane,
  FaSpinner,
  FaUserPlus,
  FaUsers,
} from "react-icons/fa";

import useScrollToTop from "../../../hooks/useScrollToTop";
import Reveal from "../../../components/Reveal/Reveal";
import ScrollIndicator from "../../../components/ScrollIndicator/ScrollIndicator";

import Footer from "../HomePage/Footer";

import "../HomePage/Home.css";

const GOOGLE_FORM_PUBLIC_URL =
  "https://docs.google.com/forms/d/e/1FAIpQLScFOEsRxz8YGKZ3EXlFgrja3nQo7PjrprJ-A9Y0_0dG6fUNTg/viewform?usp=send_form";

const GOOGLE_FORM_EMBED_URL =
  "https://docs.google.com/forms/d/e/1FAIpQLScFOEsRxz8YGKZ3EXlFgrja3nQo7PjrprJ-A9Y0_0dG6fUNTg/viewform?embedded=true";

const FORM_HEIGHT = 1780;

const BENEFITS = [
  {
    icon: FaClipboardList,
    title: "Formulaire officiel",
    text: "Les informations sont transmises directement via le Google Form du club.",
  },
  {
    icon: FaUsers,
    title: "Inscription simplifiée",
    text: "La famille complète une seule demande avec les informations attendues par le club.",
  },
  {
    icon: FaCheckCircle,
    title: "Suivi facilité",
    text: "Le club récupère les réponses au même endroit pour traiter les adhésions.",
  },
];

function InfoCard({ icon: Icon, title, text }) {
  return (
    <div className="rounded-[2rem] border border-slate-200 bg-white/85 p-5 shadow-sm backdrop-blur-xl transition hover:-translate-y-1 hover:shadow-xl dark:border-white/10 dark:bg-white/5">
      <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
        <Icon />
      </div>

      <h3 className="font-barlow text-xl font-bold text-slate-950 dark:text-white">
        {title}
      </h3>

      <p className="mt-2 text-sm leading-6 text-slate-500 dark:text-slate-400">
        {text}
      </p>
    </div>
  );
}

function FormLoadingOverlay({ isLoading }) {
  if (!isLoading) return null;

  return (
    <div className="absolute inset-0 z-10 flex items-start justify-center bg-white/80 px-4 pt-16 backdrop-blur-sm dark:bg-slate-950/80">
      <div className="inline-flex items-center gap-3 rounded-full border border-slate-200 bg-white px-5 py-3 text-sm font-bold text-slate-700 shadow-xl dark:border-white/10 dark:bg-slate-900 dark:text-slate-200">
        <FaSpinner className="animate-spin text-primary" />
        Chargement du formulaire...
      </div>
    </div>
  );
}

export default function InscriptionGoogleFormPage() {
  useScrollToTop();

  const [iframeLoading, setIframeLoading] = useState(true);

  return (
    <div className="min-h-screen overflow-x-hidden bg-slate-50 text-slate-950 dark:bg-slate-950 dark:text-white">
      <main>
        <section className="relative overflow-hidden bg-slate-950 px-4 pb-16 pt-36 text-white sm:px-6 lg:px-8 lg:pb-20 lg:pt-40">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(242,113,49,0.30),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.22),transparent_32%)]" />
          <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.08)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.08)_1px,transparent_1px)] bg-[size:44px_44px] opacity-20" />

          <div className="relative mx-auto grid max-w-7xl gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:items-end">
            <Reveal className="max-w-3xl">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.22em] text-white/70 backdrop-blur-xl">
                <FaGoogle className="text-primary" />
                Inscription en ligne
              </div>

              <h1 className="mt-6 font-barlow text-5xl font-bold leading-tight tracking-tight text-white sm:text-6xl lg:text-7xl">
                Rejoindre le club
                <br />
                via le formulaire
              </h1>

              <p className="mt-6 max-w-2xl text-base leading-8 text-white/75 sm:text-lg">
                Complétez le formulaire d’inscription officiel du club. Les
                informations seront transmises directement à l’équipe chargée
                des adhésions.
              </p>

              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <a
                  href="#formulaire-inscription"
                  className="inline-flex items-center justify-center gap-3 rounded-full bg-primary px-6 py-3.5 text-sm font-bold text-white shadow-[0_18px_45px_rgba(242,113,49,0.30)] transition hover:-translate-y-0.5 hover:brightness-110"
                >
                  Remplir le formulaire
                  <FaArrowRight />
                </a>

                <a
                  href={GOOGLE_FORM_PUBLIC_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center gap-3 rounded-full border border-white/15 bg-white/10 px-6 py-3.5 text-sm font-bold text-white backdrop-blur-xl transition hover:-translate-y-0.5 hover:bg-white/15"
                >
                  Ouvrir dans Google Forms
                  <FaExternalLinkAlt className="text-xs" />
                </a>
              </div>
            </Reveal>

            <Reveal
              variant="zoom"
              delay={120}
              className="rounded-[2.5rem] border border-white/10 bg-white/10 p-5 shadow-2xl backdrop-blur-xl"
            >
              <div className="rounded-[2rem] bg-white p-5 text-slate-950 dark:bg-slate-900 dark:text-white">
                <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-2xl text-primary">
                  <FaPaperPlane />
                </div>

                <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-400">
                  Demande d’adhésion
                </p>

                <h2 className="mt-2 font-barlow text-3xl font-bold">
                  Formulaire Google intégré
                </h2>

                <p className="mt-3 text-sm leading-7 text-slate-500 dark:text-slate-400">
                  Vous pouvez remplir l’inscription directement depuis cette
                  page, sans créer de compte au préalable.
                </p>

                <div className="mt-5 grid gap-3 sm:grid-cols-2">
                  <div className="rounded-2xl bg-slate-50 p-4 dark:bg-white/5">
                    <FaMobileAlt className="mb-2 text-primary" />
                    <p className="text-sm font-bold">Compatible mobile</p>
                  </div>

                  <div className="rounded-2xl bg-slate-50 p-4 dark:bg-white/5">
                    <FaLock className="mb-2 text-primary" />
                    <p className="text-sm font-bold">Réponses centralisées</p>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        <section className="relative overflow-hidden bg-slate-50 py-20 dark:bg-slate-950 lg:py-28">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(242,113,49,0.12),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.10),transparent_32%)]" />
          <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(148,163,184,0.13)_1px,transparent_1px),linear-gradient(to_bottom,rgba(148,163,184,0.13)_1px,transparent_1px)] bg-[size:42px_42px] opacity-35 dark:opacity-15" />

          <div className="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            <Reveal className="mx-auto max-w-3xl text-center">
              <div className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-4 py-2 text-xs font-bold uppercase tracking-[0.22em] text-orange-700 shadow-sm dark:border-orange-500/20 dark:bg-orange-500/10 dark:text-orange-300">
                <FaUserPlus />
                Inscription adhérent
              </div>

              <h2 className="mt-6 font-barlow text-4xl font-bold tracking-tight text-slate-950 dark:text-white sm:text-5xl lg:text-6xl">
                Compléter le <span className="text-primary">Google Form</span>
              </h2>

              <p className="mx-auto mt-5 max-w-2xl text-base leading-8 text-slate-600 dark:text-slate-300">
                Le formulaire ci-dessous remplace l’ancien parcours
                d’inscription. En cas de difficulté d’affichage, ouvrez-le dans
                un nouvel onglet.
              </p>
            </Reveal>

            <div className="mt-12 grid gap-5 lg:grid-cols-3">
              {BENEFITS.map((item) => (
                <Reveal key={item.title} variant="zoom">
                  <InfoCard {...item} />
                </Reveal>
              ))}
            </div>

            <Reveal
              id="formulaire-inscription"
              variant="zoom"
              delay={120}
              className="mt-14 scroll-mt-32 lg:scroll-mt-36"
            >
              <div className="overflow-hidden rounded-[2.5rem] border border-slate-200 bg-white/90 shadow-2xl backdrop-blur-xl dark:border-white/10 dark:bg-slate-900/85">
                <div className="flex flex-col gap-4 border-b border-slate-200 bg-white/90 p-5 dark:border-white/10 dark:bg-slate-900/90 sm:flex-row sm:items-center sm:justify-between lg:p-6">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-400">
                      Formulaire sécurisé
                    </p>

                    <h3 className="mt-1 font-barlow text-2xl font-bold text-slate-950 dark:text-white sm:text-3xl">
                      Inscription au club
                    </h3>
                  </div>

                  <a
                    href={GOOGLE_FORM_PUBLIC_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center justify-center gap-3 rounded-full border border-slate-200 bg-white px-5 py-3 text-sm font-bold text-slate-700 shadow-sm transition hover:-translate-y-0.5 hover:border-orange-200 hover:bg-orange-50 hover:text-orange-700 dark:border-white/10 dark:bg-white/5 dark:text-slate-200 dark:hover:bg-white/10"
                  >
                    Ouvrir en plein écran
                    <FaExternalLinkAlt className="text-xs" />
                  </a>
                </div>

                <div className="relative bg-white dark:bg-slate-950">
                  <FormLoadingOverlay isLoading={iframeLoading} />

                  <iframe
                    title="Formulaire d’inscription adhérent"
                    src={GOOGLE_FORM_EMBED_URL}
                    width="100%"
                    height={FORM_HEIGHT}
                    frameBorder="0"
                    marginHeight="0"
                    marginWidth="0"
                    loading="lazy"
                    onLoad={() => setIframeLoading(false)}
                    className="block w-full bg-white"
                  >
                    Chargement…
                  </iframe>
                </div>
              </div>

              <div className="mt-5 rounded-[1.5rem] border border-slate-200 bg-white/80 p-4 text-xs leading-6 text-slate-500 shadow-sm backdrop-blur-xl dark:border-white/10 dark:bg-white/5 dark:text-slate-400">
                <div className="flex items-start gap-3">
                  <FaInfoCircle className="mt-1 shrink-0 text-primary" />
                  <p>
                    Les informations saisies dans ce formulaire sont collectées
                    via Google Forms et transmises au club pour la gestion des
                    inscriptions. Si le formulaire ne s’affiche pas correctement
                    sur mobile, utilisez le bouton “Ouvrir en plein écran”.
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        <Footer />
        <ScrollIndicator />
      </main>
    </div>
  );
}
